import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TripContent, Geo } from '../../services/api';
import './MapPanel.css';

// Fix for default marker icons in Leaflet with bundlers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

// Custom orange marker for activities (high/medium confidence)
const orangeIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
            <path fill="#f97316" stroke="#ea580c" stroke-width="1" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 12.5 12.5 28 12.5 28s12.5-15.5 12.5-28C25 5.6 19.4 0 12.5 0z"/>
            <circle fill="#fff" cx="12.5" cy="12.5" r="5"/>
        </svg>
    `),
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

// Gray marker for low confidence / approximate locations
const grayIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
            <path fill="#9ca3af" stroke="#6b7280" stroke-width="1" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 12.5 12.5 28 12.5 28s12.5-15.5 12.5-28C25 5.6 19.4 0 12.5 0z"/>
            <circle fill="#fff" cx="12.5" cy="12.5" r="5"/>
        </svg>
    `),
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

// Orange marker with plane icon for airport arrival/departure (matches activity list flight color)
const airportIcon = new L.DivIcon({
    className: 'transport-marker airport',
    html: `<div style="background:#f97316;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
        </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
});

// Blue marker with train icon for train station arrival/departure
const trainStationIcon = new L.DivIcon({
    className: 'transport-marker train',
    html: `<div style="background:#2563eb;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect width="16" height="16" x="4" y="3" rx="2"/>
            <path d="M4 11h16"/>
            <path d="M12 3v8"/>
            <path d="m8 19-2 3"/>
            <path d="m18 22-2-3"/>
            <circle cx="8" cy="15" r="1" fill="currentColor"/>
            <circle cx="16" cy="15" r="1" fill="currentColor"/>
        </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
});

// Slate marker for car/bus arrival (ground transport)
const carIcon = new L.DivIcon({
    className: 'transport-marker car',
    html: `<div style="background:#475569;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
            <circle cx="7" cy="17" r="2"/>
            <circle cx="17" cy="17" r="2"/>
        </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
});

// Teal marker for ferry/boat
const ferryIcon = new L.DivIcon({
    className: 'transport-marker ferry',
    html: `<div style="background:#0d9488;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>
            <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/>
            <path d="M12 10v4"/>
        </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
});

// Green marker for bus
const busIcon = new L.DivIcon({
    className: 'transport-marker bus',
    html: `<div style="background:#16a34a;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 6v6"/>
            <path d="M16 6v6"/>
            <path d="M2 12h20"/>
            <path d="M6 18h3"/>
            <path d="M15 18h3"/>
            <rect x="4" y="4" width="16" height="14" rx="2"/>
        </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
});

// Hotel/accommodation marker for SYSTEM_CHECKIN / SYSTEM_CHECKOUT
const hotelIcon = new L.DivIcon({
    className: 'transport-marker hotel',
    html: `<div style="background:#7c3aed;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z"/>
            <path d="M9 22v-4h6v4"/>
            <path d="M8 6h.01"/>
            <path d="M16 6h.01"/>
            <path d="M12 6h.01"/>
            <path d="M12 10h.01"/>
            <path d="M8 10h.01"/>
            <path d="M16 10h.01"/>
            <path d="M12 14h.01"/>
            <path d="M8 14h.01"/>
            <path d="M16 14h.01"/>
        </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
});

/** Transfer marker color by mode (for SYSTEM_TRANSFER with geo) */
const TRANSFER_COLORS: Record<string, string> = {
    boat: '#0d9488',
    bus: '#16a34a',
    tram: '#2563eb',
    metro: '#2563eb',
    taxi: '#475569',
    uber: '#475569',
    other: '#14b8a6',
};

/**
 * Get the transport marker icon based on arrival location type
 */
function getTransportMarkerIcon(arrivalLocationType?: string): L.DivIcon | null {
    switch (arrivalLocationType) {
        case 'AIRPORT':
            return airportIcon;
        case 'TRAIN':
            return trainStationIcon;
        case 'CAR':
            return carIcon;
        case 'BUS':
            return busIcon;
        case 'FERRY':
            return ferryIcon;
        default:
            return null;
    }
}

/** Get transfer marker icon (SYSTEM_TRANSFER) by transferDetails.mode */
function getTransferMarkerIcon(mode?: string): L.DivIcon | null {
    const color = (mode && TRANSFER_COLORS[mode]) || TRANSFER_COLORS.other;
    return L.divIcon({
        className: 'transport-marker transfer',
        html: `<div style="background:${color};color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);border-style:dashed;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M8 12h8"/>
                <path d="m12 8 4 4-4 4"/>
            </svg>
        </div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
        popupAnchor: [0, -14],
    });
}

/**
 * Check if activity has valid coordinates for map display
 * 
 * RULES:
 * - pending status = never show (no coords yet)
 * - null coords = never show
 * - (0,0) = never show (Null Island bug)
 */
function hasValidGeo(geo?: Geo): geo is Geo & { lat: number; lng: number } {
    if (!geo) return false;
    if (geo.status === 'pending') return false;
    // Handle null coords (new schema: nullable lat/lng)
    if (geo.lat === null || geo.lng === null) return false;
    if (typeof geo.lat !== 'number' || typeof geo.lng !== 'number') return false;
    // Null island check - (0,0) is always invalid
    if (geo.lat === 0 && geo.lng === 0) return false;
    return true;
}

/**
 * Only true when location is resolved (not city-center fallback / approximate).
 * Use for: showing on map, showing "View on Map" button.
 * Approximate locations mislead users (pin at city center instead of real place).
 */
function hasResolvedGeo(geo?: Geo): geo is Geo & { lat: number; lng: number } {
    if (!hasValidGeo(geo)) return false;
    if (geo!.status === 'approximate') return false;
    if (geo!.confidence === 'low') return false;
    return true;
}

/**
 * Get marker icon based on geo confidence
 */
function getMarkerIcon(geo?: Geo, isCurrentDay = true): L.Icon | L.DivIcon {
    if (!geo || geo.confidence === 'low' || geo.status === 'approximate') {
        if (!isCurrentDay) {
            return L.divIcon({
                className: 'marker-other-day-approx',
                html: `<div style="background:#d1d5db;width:10px;height:10px;border-radius:50%;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.2);"></div>`,
                iconSize: [10, 10],
                iconAnchor: [5, 5],
            });
        }
        return grayIcon;
    }
    return isCurrentDay ? orangeIcon : L.divIcon({
        className: 'marker-other-day',
        html: `<div style="background:#9ca3af;width:10px;height:10px;border-radius:50%;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.2);"></div>`,
        iconSize: [10, 10],
        iconAnchor: [5, 5],
    });
}

// Highlighted/selected marker icon (larger, with pulse effect)
const selectedIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="35" height="51" viewBox="0 0 35 51">
            <path fill="#ea580c" stroke="#c2410c" stroke-width="2" d="M17.5 0C7.84 0 0 7.84 0 17.5c0 17.5 17.5 33 17.5 33s17.5-15.5 17.5-33C35 7.84 27.16 0 17.5 0z"/>
            <circle fill="#fff" cx="17.5" cy="17.5" r="7"/>
        </svg>
    `),
    iconSize: [35, 51],
    iconAnchor: [17, 51],
    popupAnchor: [1, -44],
});

interface MapPanelProps {
    content: TripContent;
    selectedDay: { cityIdx: number; dayIdx: number };
    currentDayNumber: number;
    selectedActivityId?: string | null;
}

export default function MapPanel({ content, selectedDay, currentDayNumber, selectedActivityId }: MapPanelProps) {
    const { t } = useTranslation();
    const mapRef = useRef<L.Map | null>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const layerGroupRef = useRef<L.LayerGroup | null>(null);

    // Initialize map
    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return;

        const map = L.map(mapContainerRef.current).setView([48.8566, 2.3522], 13);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        }).addTo(map);

        layerGroupRef.current = L.layerGroup().addTo(map);
        mapRef.current = map;

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, []);

    // Store markers for selection lookup
    const markersRef = useRef<Map<string, L.Marker>>(new Map());

    // Update markers and route
    useEffect(() => {
        if (!mapRef.current || !layerGroupRef.current) return;

        const layerGroup = layerGroupRef.current;
        layerGroup.clearLayers();
        markersRef.current.clear();

        const allCoords: L.LatLngTuple[] = [];
        const currentDayCoords: L.LatLngTuple[] = [];

        // 1. Other days (Gray dots)
        content.cities.forEach((city, cityIdx) => {
            city.days.forEach((day, dayIdx) => {
                const isCurrentDay = cityIdx === selectedDay.cityIdx && dayIdx === selectedDay.dayIdx;

                day.activities.forEach(activity => {
                    // Skip activities without resolved geo (no city-center fallback on map)
                    if (!hasResolvedGeo(activity.geo)) return;

                    const { lat, lng } = activity.geo;
                    allCoords.push([lat, lng]);

                    if (!isCurrentDay) {
                        const markerIcon = getMarkerIcon(activity.geo, false);

                        // Security: DOM-based popup construction
                        const popupDiv = document.createElement('div');
                        popupDiv.style.minWidth = '120px';

                        const small = document.createElement('small');
                        small.style.color = '#9ca3af';
                        small.textContent = t('trip.other_day');
                        popupDiv.appendChild(small);

                        const title = document.createElement('div');
                        title.style.fontWeight = '600';
                        title.style.margin = '4px 0';
                        title.style.fontSize = '13px';
                        title.textContent = activity.title;
                        popupDiv.appendChild(title);

                        const loc = document.createElement('small');
                        loc.style.color = '#666';
                        // Show approximate indicator for low confidence
                        const locText = activity.geo?.confidence === 'low' 
                            ? `${activity.location.name} (${t('trip.approximate_location', 'approx')})`
                            : activity.location.name;
                        loc.textContent = locText;
                        popupDiv.appendChild(loc);

                        L.marker([lat, lng], { icon: markerIcon })
                            .addTo(layerGroup)
                            .bindPopup(popupDiv);
                    }
                });
            });
        });

        // 2. Current day (Orange/gray markers, no numbers)
        const currentCity = content.cities[selectedDay.cityIdx];
        const currentDay = currentCity?.days[selectedDay.dayIdx];

        if (currentDay) {
            currentDay.activities.forEach((activity) => {
                // Skip activities without resolved geo (no city-center fallback on map)
                if (!hasResolvedGeo(activity.geo)) return;

                const { lat, lng } = activity.geo;
                currentDayCoords.push([lat, lng]);

                // SYSTEM_TRANSFER with geo: show transfer marker (color by mode)
                const isTransfer = activity.blockType === 'SYSTEM_TRANSFER';
                const transferIcon = isTransfer ? getTransferMarkerIcon((activity as any).transferDetails?.mode) : null;
                if (transferIcon) {
                    const popupDiv = document.createElement('div');
                    popupDiv.style.minWidth = '150px';
                    const badge = document.createElement('span');
                    badge.style.cssText = 'background:#14b8a6;color:white;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;';
                    badge.textContent = t('trip.transfer', 'Transfer');
                    popupDiv.appendChild(badge);
                    const title = document.createElement('div');
                    title.style.fontWeight = '600';
                    title.style.margin = '8px 0 4px';
                    const td = (activity as any).transferDetails;
                    title.textContent = td ? `${td.from} → ${td.to}` : activity.title;
                    popupDiv.appendChild(title);
                    const time = document.createElement('small');
                    time.style.color = '#666';
                    time.textContent = activity.time;
                    popupDiv.appendChild(time);
                    L.marker([lat, lng], { icon: transferIcon }).addTo(layerGroup).bindPopup(popupDiv);
                    return;
                }

                // SYSTEM_CHECKIN / SYSTEM_CHECKOUT: hotel icon (no number)
                const isHotel = activity.blockType === 'SYSTEM_CHECKIN' || activity.blockType === 'SYSTEM_CHECKOUT';
                if (isHotel) {
                    const popupDiv = document.createElement('div');
                    popupDiv.style.minWidth = '150px';
                    const badge = document.createElement('span');
                    badge.style.cssText = 'background:#7c3aed;color:white;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;';
                    badge.textContent = activity.blockType === 'SYSTEM_CHECKIN'
                        ? t('trip.check_in', 'Check-in')
                        : t('trip.check_out', 'Check-out');
                    popupDiv.appendChild(badge);
                    const title = document.createElement('div');
                    title.style.fontWeight = '600';
                    title.style.margin = '8px 0 4px';
                    title.textContent = activity.location?.name || activity.title;
                    popupDiv.appendChild(title);
                    const time = document.createElement('small');
                    time.style.color = '#666';
                    time.textContent = activity.time;
                    popupDiv.appendChild(time);
                    L.marker([lat, lng], { icon: hotelIcon }).addTo(layerGroup).bindPopup(popupDiv);
                    return;
                }

                // SYSTEM_ARRIVAL / SYSTEM_DEPARTURE: transport-specific icon
                const isArrivalDeparture = activity.blockType === 'SYSTEM_ARRIVAL' || activity.blockType === 'SYSTEM_DEPARTURE';
                const transportIcon = isArrivalDeparture
                    ? getTransportMarkerIcon((activity as any).arrivalLocationType)
                    : null;

                if (transportIcon) {
                    // Security: DOM-based popup construction
                    const popupDiv = document.createElement('div');
                    popupDiv.style.minWidth = '150px';

                    const badge = document.createElement('span');
                    badge.style.cssText = 'background:#f97316;color:white;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;';
                    badge.textContent = activity.blockType === 'SYSTEM_ARRIVAL' 
                        ? t('trip.arrival', 'Arrival') 
                        : t('trip.departure', 'Departure');
                    popupDiv.appendChild(badge);

                    const title = document.createElement('div');
                    title.style.fontWeight = '600';
                    title.style.margin = '8px 0 4px';
                    title.textContent = activity.location.name;
                    popupDiv.appendChild(title);

                    const time = document.createElement('small');
                    time.style.color = '#666';
                    time.textContent = activity.time;
                    popupDiv.appendChild(time);

                    L.marker([lat, lng], { icon: transportIcon })
                        .addTo(layerGroup)
                        .bindPopup(popupDiv);
                    
                    return; // Don't add number marker for transport points
                }

                const isApproximate = activity.geo?.confidence === 'low' || activity.geo?.status === 'approximate';

                // Security: DOM-based popup construction
                const popupDiv = document.createElement('div');
                popupDiv.style.minWidth = '150px';

                const time = document.createElement('strong');
                time.style.color = isApproximate ? '#9ca3af' : '#f97316';
                time.textContent = activity.time;
                popupDiv.appendChild(time);

                const title = document.createElement('div');
                title.style.fontWeight = '600';
                title.style.margin = '4px 0';
                title.textContent = activity.title;
                popupDiv.appendChild(title);

                const loc = document.createElement('small');
                loc.style.color = '#666';
                // Show approximate indicator for low confidence
                const locText = isApproximate
                    ? `📍 ${activity.location.name} (${t('trip.near_city_center', 'near city center')})`
                    : activity.location.name;
                loc.textContent = locText;
                popupDiv.appendChild(loc);

                // Check if this activity is selected
                const isSelected = selectedActivityId === activity.id;

                // Marker icon based on confidence and selection state
                const markerIcon = isSelected ? selectedIcon : getMarkerIcon(activity.geo, true);
                const marker = L.marker([lat, lng], { icon: markerIcon })
                    .addTo(layerGroup)
                    .bindPopup(popupDiv);

                // Store marker reference for selection lookup
                markersRef.current.set(activity.id, marker);
            });

            // Route line
            if (currentDayCoords.length > 1) {
                L.polyline(currentDayCoords, {
                    color: '#f97316',
                    weight: 3,
                    opacity: 0.7,
                    dashArray: '10, 10',
                }).addTo(layerGroup);
            }
        }

        // Fit bounds
        if (currentDayCoords.length > 0) {
            mapRef.current.fitBounds(L.latLngBounds(currentDayCoords), { padding: [50, 50] });
        } else if (allCoords.length > 0) {
            mapRef.current.fitBounds(L.latLngBounds(allCoords), { padding: [50, 50] });
        }
    }, [content, selectedDay, selectedActivityId, t]);

    // Pan to selected activity when selectedActivityId changes
    useEffect(() => {
        if (!mapRef.current || !selectedActivityId) return;

        const marker = markersRef.current.get(selectedActivityId);
        if (marker) {
            const latLng = marker.getLatLng();
            mapRef.current.flyTo(latLng, 15, { duration: 0.5 });
            
            // Open popup after a short delay to let the map pan
            setTimeout(() => {
                marker.openPopup();
            }, 300);
        }
    }, [selectedActivityId]);

    const currentCity = content.cities[selectedDay.cityIdx];
    const currentDay = currentCity?.days[selectedDay.dayIdx];

    return (
        <main className="map-section">
            <div className="map-container" ref={mapContainerRef} />

            <div className="map-legend">
                <div className="legend-item">
                    <span className="legend-dot orange" />
                    <span>{t('trip.current_day')}</span>
                </div>
                <div className="legend-item">
                    <span className="legend-dot gray" />
                    <span>{t('trip.other_days')}</span>
                </div>
                <div className="legend-item">
                    <span className="legend-dot blue" />
                    <span>{t('trip.transport_point', 'Transport')}</span>
                </div>
                <div className="legend-item">
                    <span className="legend-route" />
                    <span>{t('trip.route')}</span>
                </div>
            </div>

            <div className="day-summary-bar">
                <span className="summary-label">{t('trip.day')} {currentDayNumber}: {currentCity?.name}</span>
                <div className="summary-activities">
                    {currentDay?.activities.map((activity, idx) => (
                        <div key={idx} className="summary-activity">
                            <span className="summary-time">{activity.time}</span>
                            <span className="summary-title">{activity.title}</span>
                        </div>
                    ))}
                </div>
            </div>
        </main>
    );
}
